import type { ReactNode } from "react";

/**
 * mentions.tsx — shared @mention helpers for the comment UI (workspace members only).
 *
 * Storage format mirrors the worker (worker/lib/mentions.js):
 *     @[Display Name](u:USER_ID)
 * The token lives INSIDE the comment `content` string, so nothing about the
 * comment API changes — the token just travels with the text.
 *
 * Three jobs:
 *   - mentionTokensToDisplay : token → "@Name"  (what the user sees while typing/editing)
 *   - convertMentionsToTokens : "@Name" → token (what we send on submit, resolved against the member list)
 *   - renderCommentContent   : token → styled chip node (what readers see)
 */

export type MentionMember = { id: string; name: string; role?: string };

// Keep in lockstep with worker/lib/mentions.js MENTION_TOKEN_RE.
const MENTION_RE = /@\[([^\]]+)\]\(u:([A-Za-z0-9_-]+)\)/g;

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Stored tokens → plain "@Name" for display in a textarea (compose edit / draft init). */
export function mentionTokensToDisplay(content: string | null | undefined): string {
  if (!content) return content ?? "";
  return content.replace(MENTION_RE, (_full, name) => `@${name}`);
}

/**
 * Plain "@Name" → stored tokens, resolving each against the known member list.
 * Only names that EXACTLY match a member become tokens; anything else stays as
 * typed. Longer names are matched first so "@Priya Sharma" isn't half-consumed
 * by a member named "Priya".
 *
 * The leading `(^|[^\w@])` boundary keeps "email@Priya" from matching, without
 * relying on lookbehind (older Safari lacks it). Idempotent enough for our flow:
 * drafts hold display text (no raw tokens) before this runs.
 */
export function convertMentionsToTokens(
  text: string,
  members: MentionMember[] | undefined,
): string {
  if (!text || !members?.length) return text ?? "";
  const sorted = members
    .filter((m) => m?.name)
    .sort((a, b) => b.name.length - a.name.length);
  let out = text;
  for (const m of sorted) {
    const re = new RegExp(`(^|[^\\w@])@${escapeRegex(m.name)}(?![\\w])`, "g");
    out = out.replace(re, (_full, pre) => `${pre}@[${m.name}](u:${m.id})`);
  }
  return out;
}

/**
 * Render a stored comment body, turning @[Name](u:id) tokens into highlighted
 * chips and leaving the rest as plain text. Returns a node array (React renders
 * a mixed string/element array fine; only the elements need keys).
 */
export function renderCommentContent(content: string | null | undefined): ReactNode {
  if (!content) return content ?? "";
  const re = new RegExp(MENTION_RE.source, "g");
  const nodes: ReactNode[] = [];
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(content)) !== null) {
    if (m.index > last) nodes.push(content.slice(last, m.index));
    nodes.push(
      <span
        key={`m${key++}`}
        className="rounded bg-brand/15 px-1 font-medium text-brand"
      >
        @{m[1]}
      </span>,
    );
    last = re.lastIndex;
  }
  if (nodes.length === 0) return content;
  if (last < content.length) nodes.push(content.slice(last));
  return nodes;
}
